import { LocalStorageKey } from '@/constants';

const { hasVisited, cookieBannerDisplayed } = LocalStorageKey;

export const setCookieBannerClosed = (): void => {
  if (typeof window !== 'undefined' && !localStorage.getItem(cookieBannerDisplayed))
    localStorage.setItem(cookieBannerDisplayed, '1');
};

export const getCookieBannerClosed = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(cookieBannerDisplayed);
  }
  return null;
};

export const setHasVisited = (): void => {
  if (typeof window !== 'undefined' && !localStorage.getItem(hasVisited)) localStorage.setItem(hasVisited, '1');
};

const getHasVisited = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(hasVisited);
  }
  return null;
};

const hasQuantcast = () => {
  return document.getElementById('qc-cmp2-container');
};

export const showCookieBannerPopup = (): boolean => {
  const hasVisited = getHasVisited();
  const cookieBannerClosed = getCookieBannerClosed();

  if (hasQuantcast()) return false;
  if (hasVisited === '1' && cookieBannerClosed === '1') return false;
  return true;
};

type GetLocalStorageByKey = (key: string, defaultValue?: string) => string;

export const getLocalStorageByKey: GetLocalStorageByKey = (key, defaultValue = '') => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(key) || defaultValue;
  }
  return defaultValue;
};

type SetLocalStorageByKey = (key: string, value?: string) => void;

export const setLocalStorageByKey: SetLocalStorageByKey = (key, value = '') => {
  if (typeof window !== 'undefined') {
    return localStorage.setItem(key, value);
  }
};

type DeleteLocalStorageByKey = (key: string) => void;

export const deleteLocalStorageByKey: DeleteLocalStorageByKey = (key) => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(key);
  }
};

// Session storage utilities (for GA4-style session management)
export interface StorageSession {
  sessionId: string;
  userId?: string;
  clientId?: string;
  startTime: number;
  lastActivityTime: number;
  platform: 'web' | 'mobile';
  isActive: boolean;
}

export const getStoredSession = (storageKey: string): StorageSession | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    // Try sessionStorage first (GA4 behavior - persists in same tab/window)
    let storedData = sessionStorage.getItem(storageKey);
    if (storedData) {
      return JSON.parse(storedData) as StorageSession;
    }

    // Fallback to localStorage for cross-tab persistence (like GA4)
    storedData = localStorage.getItem(storageKey);
    if (storedData) {
      const session = JSON.parse(storedData) as StorageSession;
      // Migrate to sessionStorage for proper GA4-style behavior
      sessionStorage.setItem(storageKey, storedData);
      localStorage.removeItem(storageKey);
      return session;
    }

    return null;
  } catch {
    console.error('Affiliate Session Error: Error parsing or storage not available');
    return null; // Error parsing or storage not available
  }
};

export const storeSession = (storageKey: string, session: StorageSession): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const sessionData = JSON.stringify(session);
    // Use sessionStorage for GA4-style session persistence
    sessionStorage.setItem(storageKey, sessionData);
  } catch {
    // Storage not available (incognito restrictions, etc.)
    console.error('Affiliate Session Error: Storage not available');
  }
};

export const clearStoredSession = (storageKey: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(storageKey);
    localStorage.removeItem(storageKey); // Clean up old format
  } catch {
    // Storage not available
    console.error('Affiliate Session Error: Storage not available');
  }
};

export const getOrCreateClientId = (clientIdKey: string, generateIdFn: () => string): string => {
  if (typeof window === 'undefined') {
    return generateIdFn(); // Fallback for SSR
  }

  try {
    let clientId = localStorage.getItem(clientIdKey);
    if (!clientId) {
      clientId = generateIdFn();
      localStorage.setItem(clientIdKey, clientId);
    }
    return clientId;
  } catch {
    // Fallback if localStorage is not available (incognito restrictions)
    return generateIdFn();
  }
};
